// Define the VisualDots extension
function VisualDots(Splide, Components) {
  const { root } = Components.Elements;
  let container;
  let dots = [];

  function mount() {
    // 1. Create the outer wrapper
    container = document.createElement('div');
    container.className = 'wf-worm-dots';
    container.setAttribute('aria-hidden', 'true'); // Ensures screen readers ignore it entirely
    container.style.pointerEvents = 'none'; // Prevents any mobile taps from registering

    // 2. Generate a span for every slide
    for (let i = 0; i < Splide.length; i++) {
      let dot = document.createElement('span');
      dot.className = 'wf-dot';
      dots.push(dot);
      container.appendChild(dot);
    }

    root.appendChild(container);
    
    // Run initial update
    update();
    
    // 3. Listen to the 'move' event (faster than 'moved' for fluid animations)
    Splide.on('move', update);
  }

  function update(newIndex, prevIndex, destIndex) {
    // During a 'move' event, destIndex tells us where the slider is going. 
    // On initial mount, we fall back to Splide.index.
    let activeIndex = destIndex !== undefined ? destIndex : Splide.index;
    
    // Toggle the active class on the spans
    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
    });
  }

  return {
    mount,
    destroy() {
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
      dots = [];
    },
  };
}

// Sets up custom play/pause buttons for Wistia <wistia-player> components (Aurora)
// Accepts the specific 'section' element to scope the query
function setupWistiaControls(section) {
  const wistiaControlButtons = section.querySelectorAll(
    '.wistia__content .play-pause__toggle, .custom-video-container .play-pause__toggle'
  );

  wistiaControlButtons.forEach((button) => {
    // Avoid double-binding if initializeSliders runs more than once
    if (button.dataset.wistiaBound === '1') return;
    button.dataset.wistiaBound = '1';

    const contentWrapper = button.closest('.wistia__content, .custom-video-container');
    if (!contentWrapper) return;

    // Handle both Wistia players and native HTML5 videos
    const player = contentWrapper.querySelector('wistia-player, video');
    if (!player) return;

    const accessibilityText = button.querySelector('.visually-hidden');

    const syncButtonState = () => {
      const pausedOrEnded = player.paused || player.state === 'ended';
      button.classList.toggle('is-paused', pausedOrEnded);

      const label = pausedOrEnded ? 'Play video' : 'Pause video';
      if (accessibilityText) accessibilityText.textContent = label;
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-pressed', (!pausedOrEnded).toString());
    };

    // Toggle on click
    button.addEventListener('click', (e) => {
      e.preventDefault();
      if (player.paused || player.state === 'ended') {
        player.play();   
      } else {
        player.pause();  
      }
    });

    const bind = () => {
      player.removeEventListener('play', syncButtonState);
      player.removeEventListener('pause', syncButtonState);
      player.removeEventListener('ended', syncButtonState);

      player.addEventListener('play', syncButtonState);
      player.addEventListener('pause', syncButtonState);
      player.addEventListener('ended', syncButtonState);

      syncButtonState();
    };

    if (player.tagName.toLowerCase() === 'video' || typeof player.state !== 'undefined') {
      bind();
    } else {
      player.addEventListener('api-ready', bind);
    }
  });
}

// Function to initialize sliders
function initializeSliders() {
  // 1. Find ALL product sections on the page
  const productSections = document.querySelectorAll('.product[data-section-type="product"]');

  // 2. Loop through each section individually
  productSections.forEach((section) => {
    
    // Prevent double-initialization
    if (section.dataset.slidersInitialized === 'true') return;
    section.dataset.slidersInitialized = 'true';

    // 3. Find elements strictly WITHIN this specific section
    const mainSliderElement = section.querySelector('.product__slideList');
    const thumbnailSliderElement = section.querySelector('.product__thumbList');

    // Exit if sliders don't exist in this specific section
    if (!mainSliderElement || !thumbnailSliderElement) {
      return;
    }

    var main = new Splide(mainSliderElement, {
      type: 'slide',
      rewind: false,
      pagination: false,
      arrows: false,
      breakpoints: {
        1024: {
          type: 'loop',
          updateOnMove: true,
          padding: { left: '5rem', right: '5rem' },
        },
        768: {
          type: 'loop',
          updateOnMove: true,
          padding: { left: '5rem', right: '5rem' },
        },
      },
    });

    var thumbnails = new Splide(thumbnailSliderElement, {
      fixedWidth: 191,
      fixedHeight: 254,
      height: 746,
      gap: 10,
      rewind: false,
      pagination: false,
      cover: true,
      isNavigation: true,
      direction: 'ttb',
      breakpoints: {
        1024: {
          direction: 'ltr',
        },
        768: {
          destroy: true,
        },
      },
    });

    // Function to pause all Wistia videos in this section
    function pauseAllWistiaVideos() {
      var wistiaEmbeds = section.querySelectorAll('.wistia_embed_new');
      wistiaEmbeds.forEach(function (wistiaEmbed) {
        if (wistiaEmbed && typeof wistiaEmbed.pause === 'function') {
          wistiaEmbed.pause();
        }
      });
    }

    // Function to play Wistia video in active slide in THIS section
    function playActiveSlideVideo() {
      var activeIndex = main.index;
      var slides = main.Components.Elements.slides;
      var activeSlide = slides[activeIndex];
      var wistiaEmbed = activeSlide.querySelector('.wistia_embed_new');

      if (wistiaEmbed && typeof wistiaEmbed.play === 'function') {
        wistiaEmbed.play();
      }
    }

    main.on('move', pauseAllWistiaVideos);
    main.on('moved', playActiveSlideVideo);
    main.sync(thumbnails);

    main.mount();
    thumbnails.mount();

    // Mount the custom dots extension
    var dotsExtension = new VisualDots(main, main.Components);
    dotsExtension.mount();

    main.on('destroy', function () {
      dotsExtension.destroy();
    });

    // 4. Handle variant changes scoped to THIS section
    function setupVariantImageSwitching() {
      // 1. Get the section ID to uniquely identify the JSON block
      const sectionId = section.getAttribute('data-section-id');
      
      // 2. Query the document (not the section) because the JSON block is outside the <section> tag
      const productJsonElement = document.getElementById('ProductJson-' + sectionId);
      
      // 3. Look for the form inside the section, fallback to document just in case
      const productForm = section.querySelector('form[action^="/cart/add"]') || document.querySelector('form[action^="/cart/add"]');
      
      // Bail out if either is missing
      if (!productJsonElement || !productForm) {
        console.warn('Splide Variant Switching: Could not find JSON or Form for section ' + sectionId);
        return;
      }

      const productJson = JSON.parse(productJsonElement.textContent);

     const updateSliderForVariant = (variantId) => {
        if (!variantId) return;

        const selectedVariant = productJson.variants.find(variant => variant.id == variantId);

        const media = selectedVariant?.featured_media || selectedVariant?.featured_image;

        if (media && media.id) {
          const featuredMediaId = media.id;
          const targetSlide = mainSliderElement.querySelector(`.splide__slide:not(.splide__slide--clone)[data-option="${featuredMediaId}"]`);

          if (targetSlide) {
            const slides = Array.from(mainSliderElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)'));
            const slideIndex = slides.indexOf(targetSlide);

            if (slideIndex > -1) {
              main.go(slideIndex);
            }
          }
        }
      };

      // Listen for variant changes
      productForm.addEventListener('change', () => {
        // Slight delay ensures we read the FormData after the theme updates the hidden input
        setTimeout(() => {
          const selectedVariantId = new FormData(productForm).get('id');
          updateSliderForVariant(selectedVariantId);
        }, 50);
      });

      // Set the initial slide on page load
      const initialVariantId = new FormData(productForm).get('id');
      updateSliderForVariant(initialVariantId);
    }

    setupVariantImageSwitching();
    
    // Call Wistia setup passing the section container
    setupWistiaControls(section);
  });
}

// Add event listeners
document.addEventListener('DOMContentLoaded', initializeSliders);
document.addEventListener('shopify:section:load', initializeSliders);